"use client"

import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { ArrowLeft } from "lucide-react"
import { MobileBottomNav } from "../components/MobileBottomNav"
import { useAuth } from "../hooks/useAuth"
import Footer from "../components/Footer"
import { motion } from "framer-motion"

export default function TermsConditionsPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [activeSection, setActiveSection] = useState("introduction")

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  }

  const sections = [
    { id: "introduction", title: "Introduction" },
    { id: "definitions", title: "Definitions" },
    { id: "account", title: "User Account" },
    { id: "booking", title: "Booking Process" },
    { id: "payments", title: "Payments & Fees" },
    { id: "cancellation", title: "Cancellation Policy" },
    { id: "conduct", title: "User Conduct" },
    { id: "liability", title: "Limitation of Liability" },
    { id: "privacy", title: "Privacy" },
    { id: "intellectual", title: "Intellectual Property" },
    { id: "termination", title: "Termination" },
    { id: "changes", title: "Changes to Terms" },
    { id: "governing", title: "Governing Law" },
    { id: "contact", title: "Contact Information" },
  ]

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: "smooth" })
      setActiveSection(sectionId)
    }
  }

  // Track scroll position to update active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100

      sections.forEach(({ id }) => {
        const element = document.getElementById(id)
        if (element) {
          const { offsetTop, offsetHeight } = element
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(id)
          }
        }
      })
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  return (
    <div className="min-h-screen bg-[#EDEFEF]">
      {/* Header */}
      {isMobile ? (
        <header className="bg-black text-white p-4 sticky top-0 z-50">
          <div className="flex items-center">
            <button onClick={() => navigate(-1)} className="mr-4">
              <ArrowLeft className="h-6 w-6" />
            </button>
            <h1 className="text-xl font-bold">Terms & Conditions</h1>
          </div>
        </header>
      ) : (
        <header className="bg-black text-white p-4 sticky top-0 z-50">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex justify-between items-center">
              <Link to="/">
                <h1 className="text-2xl font-bold">VillaKosh</h1>
              </Link>
              <nav className="flex items-center space-x-4">
                <Link to="/" className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out">
                  Home
                </Link>
                <Link to="/about" className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out">
                  About
                </Link>
                <Link to="/contact" className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out">
                  Contact
                </Link>
              </nav>
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <motion.main
        className={`container mx-auto px-4 py-8 ${isMobile ? "pb-24" : ""}`}
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar for desktop */}
          {!isMobile && (
            <motion.aside className="md:w-1/4 lg:w-1/5" variants={itemVariants}>
              <div className="bg-white rounded-lg shadow-md p-4 sticky top-24">
                <h3 className="text-lg font-semibold mb-4">Table of Contents</h3>
                <nav>
                  <ul className="space-y-2">
                    {sections.map((section) => (
                      <li key={section.id}>
                        <button
                          onClick={() => scrollToSection(section.id)}
                          className={`text-left w-full px-3 py-2 rounded-md transition-colors ${
                            activeSection === section.id
                              ? "bg-gray-100 text-black font-medium"
                              : "text-gray-600 hover:bg-gray-50"
                          }`}
                        >
                          {section.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </motion.aside>
          )}

          {/* Main content */}
          <motion.div className="md:w-3/4 lg:w-4/5" variants={itemVariants}>
            <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
              <h1 className="text-3xl font-bold mb-6">Terms and Conditions</h1>
              <p className="text-gray-600 mb-8">Last Updated: April 1, 2025</p>

              <section id="introduction" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
                <p className="text-gray-700 mb-4">
                  Welcome to VillaKosh. These Terms and Conditions govern your use of the VillaKosh website, mobile
                  applications, and services. By accessing or using VillaKosh, you agree to be bound by these Terms. If
                  you disagree with any part of these terms, you may not access our services.
                </p>
                <p className="text-gray-700">
                  VillaKosh provides an online platform that connects property owners who have accommodations to rent
                  with guests seeking to rent such accommodations. VillaKosh is not a property owner or property manager
                  and does not own, sell, resell, furnish, provide, rent, re-rent, or manage any properties.
                </p>
              </section>

              <section id="definitions" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">2. Definitions</h2>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>
                    <strong>"VillaKosh"</strong> refers to our company, website, mobile applications, and services.
                  </li>
                  <li>
                    <strong>"User"</strong> refers to any individual who accesses or uses VillaKosh, including guests
                    and property owners.
                  </li>
                  <li>
                    <strong>"Guest"</strong> refers to a user who books or inquires about a property through VillaKosh.
                  </li>
                  <li>
                    <strong>"Property Owner"</strong> refers to a user who lists their property for rent on VillaKosh.
                  </li>
                  <li>
                    <strong>"Listing"</strong> refers to a property advertised as available for rent on VillaKosh.
                  </li>
                  <li>
                    <strong>"Booking"</strong> refers to a reservation of a property made through VillaKosh.
                  </li>
                </ul>
              </section>

              <section id="account" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">3. User Account</h2>
                <p className="text-gray-700 mb-4">
                  To access certain features of VillaKosh, you may be required to register for an account. You must
                  provide accurate, current, and complete information during the registration process and keep your
                  account information updated.
                </p>
                <p className="text-gray-700 mb-4">
                  You are responsible for maintaining the confidentiality of your account credentials and for all
                  activities that occur under your account. You agree to notify VillaKosh immediately of any
                  unauthorized use of your account.
                </p>
                <p className="text-gray-700">
                  VillaKosh reserves the right to suspend or terminate your account if any information provided during
                  registration or thereafter proves to be inaccurate, not current, or incomplete.
                </p>
              </section>

              <section id="booking" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">4. Booking Process</h2>
                <p className="text-gray-700 mb-4">
                  When you book a property through VillaKosh, you are entering into an agreement with the Property
                  Owner, not with VillaKosh. VillaKosh acts as an intermediary between Guests and Property Owners.
                </p>
                <p className="text-gray-700 mb-4">
                  By making a booking, you agree to pay all fees associated with your reservation, including the rental
                  fee, security deposit (if applicable), service fee, and any additional fees specified in the listing.
                </p>
                <p className="text-gray-700">
                  Property Owners are solely responsible for the accuracy of their listings, including descriptions,
                  amenities, availability, and pricing. VillaKosh does not verify the accuracy of listings and is not
                  responsible for any discrepancies.
                </p>
              </section>

              <section id="payments" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">5. Payments & Fees</h2>
                <p className="text-gray-700 mb-4">
                  All payments are processed through VillaKosh's secure payment system. VillaKosh charges a service fee
                  to both Guests and Property Owners for the use of our platform.
                </p>
                <p className="text-gray-700 mb-4">
                  Guest service fees are calculated as a percentage of the booking subtotal and are added to the total
                  amount paid by the Guest. Property Owner service fees are deducted from the payout to the Property
                  Owner.
                </p>
                <p className="text-gray-700">
                  VillaKosh reserves the right to modify its fee structure at any time. Any changes to fees will not
                  affect bookings made prior to the change.
                </p>
              </section>

              <section id="cancellation" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">6. Cancellation Policy</h2>
                <p className="text-gray-700 mb-4">
                  Cancellation policies are set by Property Owners and vary by listing. The specific cancellation policy
                  for a property is displayed on the listing page before booking.
                </p>
                <p className="text-gray-700 mb-4">
                  If a Guest cancels a booking, the refund amount will be determined by the property's cancellation
                  policy. VillaKosh service fees are non-refundable except in certain circumstances as determined by
                  VillaKosh.
                </p>
                <p className="text-gray-700">
                  If a Property Owner cancels a confirmed booking, VillaKosh will assist the Guest in finding
                  alternative accommodations. Property Owners who cancel confirmed bookings may be subject to penalties.
                </p>
              </section>

              <section id="conduct" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">7. User Conduct</h2>
                <p className="text-gray-700 mb-4">
                  Users agree to use VillaKosh in compliance with all applicable laws and regulations. Users shall not:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Use VillaKosh for any illegal or unauthorized purpose</li>
                  <li>Violate any laws in your jurisdiction</li>
                  <li>Infringe upon the rights of others</li>
                  <li>Interfere with or disrupt VillaKosh or servers or networks connected to VillaKosh</li>
                  <li>Post false, inaccurate, misleading, defamatory, or offensive content</li>
                  <li>Create multiple accounts or listings for improper purposes</li>
                  <li>Transfer your account to another party without VillaKosh's consent</li>
                </ul>
              </section>

              <section id="liability" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">8. Limitation of Liability</h2>
                <p className="text-gray-700 mb-4">
                  VillaKosh is not responsible for the conduct, online or offline, of any User of VillaKosh. Users agree
                  to release VillaKosh from any claims, demands, and damages arising out of or in connection with
                  disputes between Users.
                </p>
                <p className="text-gray-700 mb-4">
                  VillaKosh does not own, create, sell, resell, provide, control, manage, offer, deliver, or supply any
                  listings or properties. Property Owners are independent third-party contractors and are not employees,
                  agents, or partners of VillaKosh.
                </p>
                <p className="text-gray-700">
                  To the maximum extent permitted by law, VillaKosh disclaims all warranties, express or implied,
                  including but not limited to implied warranties of merchantability, fitness for a particular purpose,
                  and non-infringement.
                </p>
              </section>

              <section id="privacy" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">9. Privacy</h2>
                <p className="text-gray-700 mb-4">
                  VillaKosh's Privacy Policy, which is incorporated into these Terms by reference, explains how we
                  collect, use, and disclose information from our Users.
                </p>
                <p className="text-gray-700">
                  By using VillaKosh, you consent to our collection, use, and disclosure of information as described in
                  our Privacy Policy.
                </p>
              </section>

              <section id="intellectual" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">10. Intellectual Property</h2>
                <p className="text-gray-700 mb-4">
                  The VillaKosh platform, including its content, features, and functionality, is owned by VillaKosh and
                  is protected by copyright, trademark, and other intellectual property laws.
                </p>
                <p className="text-gray-700 mb-4">
                  Users may not copy, modify, distribute, sell, or lease any part of VillaKosh without prior written
                  consent from VillaKosh.
                </p>
                <p className="text-gray-700">
                  By submitting content to VillaKosh, including property listings, reviews, and photos, you grant
                  VillaKosh a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish,
                  translate, and distribute such content.
                </p>
              </section>

              <section id="termination" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">11. Termination</h2>
                <p className="text-gray-700 mb-4">
                  VillaKosh reserves the right to terminate or suspend your access to VillaKosh immediately, without
                  prior notice or liability, for any reason, including but not limited to a breach of these Terms.
                </p>
                <p className="text-gray-700">
                  Upon termination, your right to use VillaKosh will immediately cease. All provisions of these Terms
                  which by their nature should survive termination shall survive termination.
                </p>
              </section>

              <section id="changes" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">12. Changes to Terms</h2>
                <p className="text-gray-700 mb-4">
                  VillaKosh reserves the right to modify these Terms at any time. We will provide notice of significant
                  changes by posting the updated Terms on our website and updating the "Last Updated" date.
                </p>
                <p className="text-gray-700">
                  Your continued use of VillaKosh after any changes to these Terms constitutes your acceptance of the
                  new Terms.
                </p>
              </section>

              <section id="governing" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">13. Governing Law</h2>
                <p className="text-gray-700 mb-4">
                  These Terms shall be governed by and construed in accordance with the laws of India, without regard to
                  its conflict of law provisions.
                </p>
                <p className="text-gray-700">
                  Any disputes arising under or in connection with these Terms shall be subject to the exclusive
                  jurisdiction of the courts located in Mumbai, India.
                </p>
              </section>

              <section id="contact" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">14. Contact Information</h2>
                <p className="text-gray-700 mb-4">If you have any questions about these Terms, please contact us at:</p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700">VillaKosh</p>
                  <p className="text-gray-700">123 Villa Street</p>
                  <p className="text-gray-700">Mumbai, India</p>
                  <p className="text-gray-700">Email: legal@villakosh.com</p>
                  <p className="text-gray-700">Phone: +91 9876543210</p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </motion.main>

      {/* Footer for desktop */}
      {!isMobile && <Footer />}

      {/* Mobile Bottom Nav */}
      {isMobile && <MobileBottomNav user={user} />}
    </div>
  )
}

